/* Atelier Tenant Layout — intimate, romantic, salon-style chrome.

   What makes this different from Editorial:
   • Centered wordmark masthead set in Playfair Display italic — feels like
     the personal letterhead of an artist, not a corporate brand bar.
   • Hairline rose-gold rule beneath, then a centered nav strip in small
     caps. The whole header reads more like the title page of a private
     portfolio book than a website nav.
   • A whisper-thin vertical rose-gold line down the LEFT edge of the
     viewport, present on every page — the calling card of a personal
     atelier (think Coco Chanel ribbon).
   • Footer is a single centered column with a hand-set sign-off
     ("with care, Studio Name") in romantic italic — intimate and personal,
     not a corporate sitemap.
*/

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTenantTheme } from "@/hooks/useTenantTheme";
import { tenantHref } from "@/lib/tenant";
import TenantSignature from "@/components/tenant/TenantSignature";
import LumetierFooterCredit from "@/components/tenant/LumetierFooterCredit";
import TenantMobileNav from "@/components/tenant/TenantMobileNav";
import TenantWordmark from "@/components/tenant/TenantWordmark";
import { Instagram, Mail } from "lucide-react";

const NAV = [
  { to: "/",          label: "Home",      end: true },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/services",  label: "Services"  },
  { to: "/contact",   label: "Contact"   },
];

const SERIF = "'Playfair Display', 'Cormorant Garamond', 'Didot', serif";

export default function AtelierTenantLayout() {
  useTenantTheme();
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const year = new Date().getFullYear();

  return (
    <div
      className="min-h-screen antialiased relative"
      style={{
        fontFamily: "var(--tenant-sans)",
        background: "var(--tenant-bg, #F5EFEA)",
        color: "var(--tenant-fg, #2A1F1A)",
      }}
      data-template="atelier"
    >
      {/* Calling-card rose-gold ribbon down the left edge — always present */}
      <div
        aria-hidden
        className="hidden md:block fixed top-0 bottom-0 left-0 w-px z-20"
        style={{ background: "var(--tenant-accent)", opacity: 0.55 }}
      />

      {/* ── Centered salon masthead ───────────────────────────────────── */}
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${scrolled ? "py-2.5 backdrop-blur-md" : "py-6"}`}
        style={{
          background: scrolled
            ? "color-mix(in srgb, var(--tenant-bg, #F5EFEA) 92%, transparent)"
            : "color-mix(in srgb, var(--tenant-bg, #F5EFEA) 70%, transparent)",
          borderBottom: scrolled ? "1px solid var(--tenant-fg-line)" : "1px solid transparent",
        }}
        data-testid="atelier-tenant-nav"
      >
        {/* Mobile-only hamburger trigger — pinned top-right of the
            centered salon masthead so the wordmark stays perfectly
            symmetric on phones. Vanishes at md+. */}
        <div className="absolute right-3 top-3 md:hidden">
          <TenantMobileNav tone="light" ctaLabel="Reserve" studioName={studioName} />
        </div>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 text-center">
          <Link
            to={tenantHref("/")}
            className={`block transition-all duration-500 ${scrolled ? "text-base" : "text-2xl lg:text-4xl"}`}
            style={{
              fontFamily: SERIF,
              fontStyle: "italic",
              color: "var(--tenant-fg)",
              letterSpacing: "0.01em",
            }}
            data-testid="atelier-tenant-wordmark"
          >
            <TenantWordmark
              logoUrl={settings?.logo_url}
              coverLogoUrl={settings?.cover_logo_url}
              tone="light"
              studioName={studioName}
              imgClassName={scrolled ? "h-7 w-auto mx-auto" : "h-10 lg:h-14 w-auto mx-auto"}
            >
              {studioName}
            </TenantWordmark>
          </Link>
          <div
            className={`mx-auto h-px transition-all duration-500 ${scrolled ? "w-0 mt-0" : "w-10 mt-3"}`}
            style={{ background: "var(--tenant-accent)" }}
          />
          <nav className={`mt-3 hidden md:flex justify-center gap-8 transition-opacity duration-300`} aria-label="Primary">
            {NAV.map((it) => (
              <NavLink
                key={it.to}
                to={tenantHref(it.to)}
                end={it.end}
                data-testid={`atelier-tenant-nav-${it.label.toLowerCase()}`}
                className="text-[10px] uppercase tracking-[0.38em] transition-colors"
                style={({ isActive }) => ({
                  color: isActive ? "var(--tenant-accent)" : "var(--tenant-fg-soft, rgba(42,31,26,0.55))",
                })}
              >
                {it.label}
              </NavLink>
            ))}
            <Link
              to={tenantHref("/booking")}
              className="text-[10px] uppercase tracking-[0.38em] transition-colors hover:opacity-100"
              style={{ color: "var(--tenant-accent)", opacity: 0.95 }}
              data-testid="atelier-tenant-book-cta"
            >
              Reserve →
            </Link>
          </nav>
        </div>
      </header>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <main key={location.pathname} className="pt-28 lg:pt-36">
        <Outlet />
      </main>

      {/* ── Hand-set intimate footer ──────────────────────────────────── */}
      <footer
        className="border-t mt-16 pt-12 pb-10"
        style={{
          borderTopColor: "var(--tenant-fg-line)",
          background: "var(--tenant-bg, #F5EFEA)",
        }}
        data-testid="atelier-tenant-footer"
      >
        <div className="max-w-[640px] mx-auto px-6 lg:px-12 text-center space-y-6">
          <div
            className="text-2xl lg:text-3xl italic"
            style={{ fontFamily: SERIF, color: "var(--tenant-fg)" }}
          >
            with care,
          </div>
          <div
            className="text-3xl lg:text-4xl"
            style={{ fontFamily: SERIF, color: "var(--tenant-accent)" }}
          >
            {studioName}
          </div>
          {settings?.tagline && (
            <p className="text-sm italic max-w-md mx-auto leading-relaxed" style={{ fontFamily: SERIF, color: "var(--tenant-fg-soft)" }}>
              {settings.tagline}
            </p>
          )}

          <div className="mx-auto h-px w-10" style={{ background: "var(--tenant-accent)", opacity: 0.6 }} />

          <div className="text-sm space-y-2" style={{ color: "var(--tenant-fg-soft)" }}>
            {settings?.contact_email && (
              <a href={`mailto:${settings.contact_email}`} className="inline-flex items-center gap-2 hover:opacity-100" style={{ opacity: 0.85 }}>
                <Mail className="w-3.5 h-3.5" strokeWidth={1.25} /> {settings.contact_email}
              </a>
            )}
            {settings?.contact_location && (
              <div className="italic" style={{ fontFamily: SERIF }}>{settings.contact_location}</div>
            )}
            {settings?.instagram_url && (
              <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:opacity-100" style={{ opacity: 0.85 }}>
                <Instagram className="w-3.5 h-3.5" strokeWidth={1.25} />
              </a>
            )}
          </div>

          <div className="pt-6 text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--tenant-fg-mute)" }}>
            <span>© {year} {studioName}</span>
            <span className="opacity-50 mx-2">·</span>
            <LumetierFooterCredit className="hover:opacity-100" testid="atelier-lumetier-credit" />
          </div>
        </div>
      </footer>

      <TenantSignature tone="light" />
    </div>
  );
}
